import {
    Autocomplete,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import FieldMapper from "../FieldMapper";
import { FieldMapping, ScopeOption } from "../fluss-builder-types";
import { FlussNodePanelData } from "./fluss-node-panel-data";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import {
    parseMapping,
    primaryBindingFor,
    serializeMapping,
    updateBinding,
} from "./fluss-node-panel-utils";
import { useCallActivityInterface } from "./useCallActivityInterface";
import { NodeNameField } from "./ActivityNodePanels";

interface CallActivityNodePanelProps {
    selection: FlussElementSelection;
    modeler: any;
    data: FlussNodePanelData;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

export const CallActivityNodePanel = ({
    selection, modeler, data, canViewAdvanced, readOnly, onChanged,
}: CallActivityNodePanelProps) => {
    const { t } = useTranslation();
    const binding = primaryBindingFor(selection.businessObject);

    const [definitionKey, setDefinitionKey] = useState(String(binding?.definitionKey || ""));
    const [versionPolicy, setVersionPolicy] = useState(String(binding?.versionPolicy || "active"));
    const [pinnedVersion, setPinnedVersion] = useState(String(binding?.pinnedVersion || ""));
    const [onChildFailure, setOnChildFailure] = useState(String(binding?.onChildFailure || "fail"));
    const [cascadeCancel, setCascadeCancel] = useState(binding?.cascadeCancel !== "false");
    const [inputRows, setInputRows] = useState<FieldMapping[]>(parseMapping(binding?.inputMappings));

    // The called process supplies destination options; existing mappings remain available
    // when their names are absent from the current required_inputs list.
    const callActivityInterfaceQuery = useCallActivityInterface(definitionKey, versionPolicy, pinnedVersion);
    const requiredInputs = callActivityInterfaceQuery.data?.required_inputs || [];

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setDefinitionKey(String(current?.definitionKey || ""));
        setVersionPolicy(String(current?.versionPolicy || "active"));
        setPinnedVersion(String(current?.pinnedVersion || ""));
        setOnChildFailure(String(current?.onChildFailure || "fail"));
        setCascadeCancel(current?.cascadeCancel !== "false");
        const mappings = parseMapping(current?.inputMappings);
        setInputRows(mappings);
        if (!readOnly && current?.outputMappings !== undefined) {
            updateBinding(selection, modeler, "call_activity", { outputMappings: undefined });
            onChanged();
        }
    }, [selection.id, selection.businessObject, readOnly]);

    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "call_activity", { outputMappings: undefined, ...updates });
        onChanged();
    };

    const publishedDefinitions = useMemo(
        () => (data.definitions || []).filter((definition) => definition.status === "published"),
        [data.definitions]
    );
    const processOptions = useMemo(() => {
        const byKey = new Map<string, { definition_key: string; name: string }>();
        publishedDefinitions.forEach((definition) => {
            if (!byKey.has(definition.definition_key)) {
                byKey.set(definition.definition_key, {
                    definition_key: definition.definition_key,
                    name: flussDefinitionLabel(t, definition.name),
                });
            }
        });
        return Array.from(byKey.values());
    }, [publishedDefinitions, t]);
    const versionsForKey = useMemo(
        () => publishedDefinitions
            .filter((definition) => definition.definition_key === definitionKey)
            .map((definition) => definition.version)
            .sort((a, b) => b - a),
        [publishedDefinitions, definitionKey]
    );

    // PRIM-019 (round 6): "" (unknown), not "TEXT" — the child process's input variable names,
    // with no real type known here.
    const inputVariableTargets: ScopeOption[] = useMemo(() =>
        Array.from(new Set([...requiredInputs, ...inputRows.map((row) => row.target)]))
            .filter(Boolean)
            .map((name) => ({ name, label: name, type: "" })),
    [inputRows, requiredInputs]);

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="call_activity"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Stack spacing={1.5}>
                        <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
                        <FlussNodePanelField label={t("fluss.node_panel.call_activity.process", { defaultValue: "Process" })}>
                            <Autocomplete
                                size="small"
                                value={processOptions.find((option) => option.definition_key === definitionKey) || null}
                                options={processOptions}
                                disabled={readOnly}
                                getOptionLabel={(option) => `${option.name} (${option.definition_key})`}
                                isOptionEqualToValue={(option, value) => option.definition_key === value.definition_key}
                                onChange={(_, option) => {
                                    const nextKey = option?.definition_key || "";
                                    setDefinitionKey(nextKey);
                                    setPinnedVersion("");
                                    change({ definitionKey: nextKey, pinnedVersion: "" });
                                }}
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </FlussNodePanelField>

                        <FlussNodePanelField label={t("fluss.node_panel.call_activity.version", { defaultValue: "Version" })}>
                            <Select
                                size="small"
                                value={versionPolicy}
                                disabled={readOnly}
                                inputProps={{ "aria-label": String(t("fluss.node_panel.call_activity.version", { defaultValue: "Version" })) }}
                                onChange={(event) => {
                                    setVersionPolicy(event.target.value);
                                    change({ versionPolicy: event.target.value });
                                }}
                            >
                                <MenuItem value="active">
                                    {t("fluss.node_panel.call_activity.version.active", { defaultValue: "Always the active version" })}
                                </MenuItem>
                                <MenuItem value="pinned">
                                    {t("fluss.node_panel.call_activity.version.pinned", { defaultValue: "Pinned version" })}
                                </MenuItem>
                            </Select>
                        </FlussNodePanelField>

                        {versionPolicy === "pinned" && (
                            <FlussNodePanelField label={t("fluss.node_panel.call_activity.pinned_version", { defaultValue: "Pinned version number" })}>
                                <Select
                                    size="small"
                                    value={pinnedVersion}
                                    disabled={readOnly || versionsForKey.length === 0}
                                    displayEmpty
                                    inputProps={{ "aria-label": String(t("fluss.node_panel.call_activity.pinned_version", { defaultValue: "Pinned version number" })) }}
                                    onChange={(event) => {
                                        setPinnedVersion(event.target.value);
                                        change({ pinnedVersion: event.target.value });
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        {t("fluss.node_panel.call_activity.select_version", { defaultValue: "Select a version" })}
                                    </MenuItem>
                                    {versionsForKey.map((version) => (
                                        <MenuItem key={version} value={String(version)}>
                                            {t("fluss.node_panel.call_activity.version_number", { version, defaultValue: "v{{version}}" })}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FlussNodePanelField>
                        )}

                        <FlussNodePanelField label={t("fluss.node_panel.call_activity.inputs", { defaultValue: "Inputs" })} fieldPath="inputMappings">
                            <Stack spacing={1}>
                                {/* PRIM-015 (round 6, call site #5): targets are the child
                                    process's input variables, not columns of a record being
                                    created — never enforce. */}
                                <FieldMapper
                                    value={inputRows}
                                    targetFields={inputVariableTargets}
                                    disabled={readOnly}
                                    enforceRequired={false}
                                    onChange={(next) => {
                                        setInputRows(next);
                                        change({ inputMappings: serializeMapping(next) });
                                    }}
                                />
                            </Stack>
                        </FlussNodePanelField>

                        {/* Return is automatic (C6): the called process's declared outputs come back as
                            <this node>.<name>. The generic read-only "Saídas" section lists them. */}
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.call_activity.outputs_automatic", {
                                defaultValue: "O retorno é automático: as saídas declaradas pelo processo chamado ficam disponíveis para as próximas etapas.",
                            })}
                        </Typography>

                        <FlussNodePanelField
                            label={t("fluss.node_panel.call_activity.on_failure", { defaultValue: "If the called process fails" })}
                            hint={t("fluss.node_panel.call_activity.on_failure_hint", {
                                defaultValue: "Both options raise the same incident at runtime; the only real difference is that \"Follow the attached error path\" requires an error boundary attached to this node to publish.",
                            })}
                        >
                            <Select
                                size="small"
                                value={onChildFailure}
                                disabled={readOnly}
                                inputProps={{ "aria-label": String(t("fluss.node_panel.call_activity.on_failure", { defaultValue: "If the called process fails" })) }}
                                onChange={(event) => {
                                    setOnChildFailure(event.target.value);
                                    change({ onChildFailure: event.target.value });
                                }}
                            >
                                <MenuItem value="fail">
                                    {t("fluss.node_panel.call_activity.on_failure.fail", {
                                        defaultValue: "Fail this process (incident) — caught by an attached error boundary, if any",
                                    })}
                                </MenuItem>
                                <MenuItem value="error_route">
                                    {t("fluss.node_panel.call_activity.on_failure.error_route", { defaultValue: "Follow the attached error path" })}
                                </MenuItem>
                                <MenuItem value="continue">
                                    {t("fluss.node_panel.call_activity.on_failure.continue", { defaultValue: "Continue anyway" })}
                                </MenuItem>
                            </Select>
                        </FlussNodePanelField>

                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={cascadeCancel}
                                    disabled={readOnly}
                                    onChange={(event) => {
                                        setCascadeCancel(event.target.checked);
                                        change({ cascadeCancel: event.target.checked ? "true" : "false" });
                                    }}
                                />
                            )}
                            label={t("fluss.node_panel.call_activity.cascade_cancel", {
                                defaultValue: "Cancel the called process if this one is cancelled",
                            })}
                        />
                    </Stack>
                ),
            }]}
        />
    );
};

export default CallActivityNodePanel;
